import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Infinity as InfinityIcon, RotateCcw, Zap, AlertTriangle, Search,
} from "lucide-react";

/**
 * v6.1 Phase 2a — Settings > Usage Caps.
 *
 * VC Admin only. Tenant-wide monthly cap on top, per-person rows below,
 * each with inline edit, No Limit toggle, Reset-to-Default, and a manual
 * Reload action available at any point within the current period.
 */
export default function UsageCaps() {
  const [state, setState] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/v61/caps");
    setState(data);
  };
  useEffect(() => { load(); }, []);

  if (!state) return (
    <div className="cw-card p-10 text-center text-[var(--cw-muted)] text-sm">Loading…</div>
  );

  const persons = state.persons.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.full_name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  });

  const period = new Date(state.period_start).toLocaleDateString(undefined, {
    month: "long", year: "numeric",
  });

  return (
    <div className="space-y-5" data-testid="usage-caps-tab">
      <div className="cw-card p-5">
        <div className="cw-caption uppercase tracking-widest">
          Monthly Token Usage
        </div>
        <p className="text-sm text-[var(--cw-muted)] mt-1">
          Two independent caps — the whole workspace, and every person — both
          in raw tokens, both resetting on the first of every month. Set either
          to <strong>No Limit</strong> to remove blocking for that scope.
          Reload a cap at any point in <strong>{period}</strong> to grant
          usage back without waiting for the automatic monthly reset.
        </p>
      </div>

      {/* Tenant */}
      <div className="cw-card p-5">
        <h3 className="cw-h3">Workspace</h3>
        {state.tenant ? (
          <CapRow
            row={{
              cap_id: state.tenant.cap_id,
              label: "Workspace-wide cap",
              is_unlimited: state.tenant.is_unlimited,
              limit_value: state.tenant.limit_value,
              used: state.tenant.used,
              percent: state.tenant.percent,
              is_default: state.tenant.is_default,
              last_reloaded_at: state.tenant.last_reloaded_at,
            }}
            defaultValue={state.defaults.tenant}
            onChanged={load}
            testidPrefix="cap-tenant"
          />
        ) : (
          <div className="text-sm text-[var(--cw-muted)]">No tenant cap set.</div>
        )}
      </div>

      {/* Persons */}
      <div className="cw-card p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="cw-h3">People</h3>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--cw-muted)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people…"
              className="h-8 pl-7 pr-3 rounded-md border text-sm w-64"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="usage-caps-search"
            />
          </div>
        </div>
        <div className="space-y-2">
          {persons.map((p) => (
            <CapRow
              key={p.cap_id}
              row={{
                cap_id: p.cap_id,
                label: p.full_name,
                subLabel: `${p.email} · ${p.role}`,
                is_unlimited: p.is_unlimited,
                limit_value: p.limit_value,
                used: p.used,
                percent: p.percent,
                is_default: p.is_default,
                last_reloaded_at: p.last_reloaded_at,
              }}
              defaultValue={state.defaults.person}
              onChanged={load}
              testidPrefix={`cap-person-${p.user_id}`}
            />
          ))}
          {persons.length === 0 && (
            <div className="text-xs text-[var(--cw-muted)] italic p-2">
              No people match this search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- CapRow --------------------------- */
function CapRow({ row, defaultValue, onChanged, testidPrefix }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.limit_value ?? defaultValue);
  const [unlimited, setUnlimited] = useState(!!row.is_unlimited);
  const [saving, setSaving] = useState(false);

  useMemo(() => { setVal(row.limit_value ?? defaultValue); setUnlimited(!!row.is_unlimited); }, [row.cap_id]); // eslint-disable-line

  const save = async () => {
    if (!unlimited && (!val || parseInt(val, 10) < 1)) {
      toast.error("Enter a positive whole number, or check No Limit.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/v61/caps/${row.cap_id}`, {
        is_unlimited: unlimited,
        limit_value: unlimited ? null : parseInt(val, 10),
      });
      toast.success("Cap updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };
  const resetDefault = async () => {
    try {
      const { data } = await api.post(`/v61/caps/${row.cap_id}/reset-default`);
      toast.success(`Reset to default (${data.new_limit.toLocaleString()} tokens)`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const reload = async () => {
    const val = window.prompt(
      "New limit (blank keeps current). This resets the 80% / 100% notifications for this period:",
      row.limit_value ?? ""
    );
    if (val === null) return;
    try {
      const body = val.trim() ? { new_limit: parseInt(val, 10) } : {};
      const { data } = await api.post(`/v61/caps/${row.cap_id}/reload`, body);
      toast.success(`Reloaded to ${data.new_limit.toLocaleString()} tokens for this period`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const pct = Math.min(100, Math.max(0, row.percent || 0));
  const barColor = pct >= 100 ? "#B02B2B" : pct >= 80 ? "#E0A100" : "#2C9A52";

  return (
    <div
      className="flex items-center gap-3 border rounded-md p-3 flex-wrap"
      style={{ borderColor: "var(--cw-border)" }}
      data-testid={`${testidPrefix}-row`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-medium">{row.label}</div>
        {row.subLabel && (
          <div className="text-[11px] text-[var(--cw-muted)]">{row.subLabel}</div>
        )}
      </div>

      <div className="w-[220px] shrink-0">
        {row.is_unlimited ? (
          <div className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
            style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}
            data-testid={`${testidPrefix}-unlimited-badge`}>
            <InfinityIcon size={12} /> No Limit — {row.used.toLocaleString()} used this month
          </div>
        ) : (
          <>
            <div className="h-2 rounded overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
              <div
                className="h-full transition"
                style={{ width: `${pct}%`, background: barColor }}
                data-testid={`${testidPrefix}-progress`}
              />
            </div>
            <div className="text-[11px] text-[var(--cw-muted)] mt-1"
              data-testid={`${testidPrefix}-progress-label`}>
              {row.used.toLocaleString()} / {row.limit_value?.toLocaleString() || 0} tokens
              {pct >= 80 && pct < 100 && (
                <span className="ml-2 text-[#8A6510]"><AlertTriangle size={10} className="inline" /> 80% used</span>
              )}
              {pct >= 100 && (
                <span className="ml-2 text-[#B02B2B]"><AlertTriangle size={10} className="inline" /> Blocked</span>
              )}
            </div>
          </>
        )}
      </div>

      {editing ? (
        <div className="flex items-center gap-2">
          <label className="text-xs inline-flex items-center gap-1">
            <input type="checkbox" checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              data-testid={`${testidPrefix}-unlimited-check`} />
            No Limit
          </label>
          <input
            type="number"
            value={unlimited ? "" : val}
            onChange={(e) => setVal(e.target.value)}
            disabled={unlimited}
            className="w-32 h-8 px-2 rounded-md border text-sm disabled:opacity-50"
            style={{ borderColor: "var(--cw-border)" }}
            placeholder={defaultValue.toLocaleString()}
            data-testid={`${testidPrefix}-limit-input`}
          />
          <button onClick={save} disabled={saving}
            className="cw-btn-primary text-xs"
            data-testid={`${testidPrefix}-save`}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={() => setEditing(false)} className="cw-btn-ghost text-xs">Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditing(true)}
            className="text-xs px-3 py-1.5 rounded-md border"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid={`${testidPrefix}-edit`}
          >
            Edit
          </button>
          <button onClick={resetDefault}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid={`${testidPrefix}-reset-default`}
            title="Restore to the platform default value">
            <RotateCcw size={11} /> Default
          </button>
          {!row.is_unlimited && (
            <button onClick={reload}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border"
              style={{ borderColor: "var(--cw-primary)", color: "var(--cw-primary)" }}
              data-testid={`${testidPrefix}-reload`}
              title="Manually reload / raise this cap within the current period">
              <Zap size={11} /> Reload
            </button>
          )}
        </div>
      )}
      {row.is_default && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
          style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
          DEFAULT
        </span>
      )}
    </div>
  );
}
